import React, { useMemo, useState } from 'react';
import { ArrowLeftRight, CopyPlus } from 'lucide-react';
import Overlay from './PlannerOverlay';
import { getPlannerDayGroups } from '../../api';
import { addDays, fmtDayShort } from './plannerShared';

// Group 1 / Group 2 for one day (rotation blocks use them). Membership changes daily, so the
// panel offers "Copy from previous day" and "Swap groups". Players in neither group are listed as
// "Not in a group". Changes after the week is published stay private until it is published again.

const COLS = [
  { id: 1, label: 'Group 1' },
  { id: 2, label: 'Group 2' },
  { id: 0, label: 'Not in a group' },
];

export default function GroupsPanel({ token, day, players, canEdit, busy, onClose, onSave }) {
  const [assign, setAssign] = useState(() => {
    const m = {};
    day.groups[1].forEach(id => { m[id] = 1; });
    day.groups[2].forEach(id => { m[id] = 2; });
    return m;
  });
  const [note, setNote] = useState(null);

  // Group members who are no longer active still show, so nobody silently disappears.
  const everyone = useMemo(() => {
    const known = new Set(players.map(p => p.id));
    const extra = [...day.groups[1], ...day.groups[2]].filter(id => !known.has(id)).map(id => ({ id, name: `Player #${id} (inactive)` }));
    return [...players, ...extra];
  }, [players, day.groups]);

  const set = (id, g) => setAssign(prev => ({ ...prev, [id]: g }));
  const swap = () => setAssign(prev => Object.fromEntries(Object.entries(prev).map(([id, g]) => [id, g === 1 ? 2 : g === 2 ? 1 : g])));
  const copyPrevious = async () => {
    try {
      const prevDate = addDays(day.date, -1);
      const g = await getPlannerDayGroups(token, prevDate);
      if (!g.group1.length && !g.group2.length) { setNote(`${fmtDayShort(prevDate)} has no groups.`); return; }
      const m = {};
      g.group1.forEach(id => { m[id] = 1; });
      g.group2.forEach(id => { m[id] = 2; });
      setAssign(m);
      setNote(`Copied from ${fmtDayShort(prevDate)}. Save to keep it.`);
    } catch (e) { setNote(e.message); }
  };

  const ids = g => everyone.filter(p => (assign[p.id] || 0) === g);
  const dirty = JSON.stringify([ids(1).map(p => p.id).sort(), ids(2).map(p => p.id).sort()])
    !== JSON.stringify([[...day.groups[1]].sort(), [...day.groups[2]].sort()]);

  return (
    <Overlay title={`Groups · ${fmtDayShort(day.date)}`} onClose={onClose} width={720}>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="pl-btn" disabled={busy} onClick={copyPrevious}><CopyPlus size={14} /> Copy from previous day</button>
          <button className="pl-btn" disabled={busy} onClick={swap}><ArrowLeftRight size={14} /> Swap groups</button>
        </div>
      )}
      {note && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{note}</div>}
      {day.groups_changed && <div style={{ fontSize: 12, color: 'var(--pl-warn-border)' }}>These groups were changed after publishing; players still see the published groups.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        {COLS.map(col => (
          <div key={col.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{col.label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({ids(col.id).length})</span></div>
            {ids(col.id).map((p, i) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ width: 18, color: 'var(--text-muted)', textAlign: 'right' }}>{col.id ? i + 1 : ''}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}{p.position === 'GK' ? ' (GK)' : ''}
                </span>
                {canEdit && COLS.filter(c => c.id !== col.id).map(c => (
                  <button key={c.id} type="button" className="pl-btn" disabled={busy}
                    style={{ minHeight: 24, padding: '1px 6px', fontSize: 11 }}
                    aria-label={`Move ${p.name} to ${c.label}`} title={`Move to ${c.label}`}
                    onClick={() => set(p.id, c.id)}>{c.id ? `G${c.id}` : '–'}</button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {canEdit && (
        <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !dirty}
          onClick={() => onSave({ group1: ids(1).map(p => p.id), group2: ids(2).map(p => p.id) })}>
          Save groups
        </button>
      )}
    </Overlay>
  );
}
